import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Search, Plus, Trash2, Edit3, ArrowLeft, Save } from 'lucide-react';
import { useSettingsStore } from '@/stores/settings-store';
import { useDialogStore } from '@/stores/dialog-store';

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

interface Snippet {
  label: string;
  sql: string;
  category: string;
}

interface CustomSnippet {
  id: string;
  label: string;
  sql: string;
  category: string;
}

// ═══════════════════════════════════════════
// Built-in snippets
// ═══════════════════════════════════════════

const BUILT_IN_SNIPPETS: Snippet[] = [
  { label: 'SELECT all', sql: 'SELECT *\nFROM table_name\nWHERE condition\nORDER BY column\nLIMIT 100;', category: 'Query' },
  { label: 'SELECT with JOIN', sql: 'SELECT t1.*, t2.*\nFROM table1 t1\nINNER JOIN table2 t2 ON t1.id = t2.table1_id\nWHERE condition;', category: 'Query' },
  { label: 'LEFT JOIN', sql: 'SELECT t1.*, t2.*\nFROM table1 t1\nLEFT JOIN table2 t2 ON t1.id = t2.table1_id;', category: 'Query' },
  { label: 'GROUP BY with COUNT', sql: 'SELECT column, COUNT(*) as count\nFROM table_name\nGROUP BY column\nORDER BY count DESC;', category: 'Aggregate' },
  { label: 'INSERT single row', sql: "INSERT INTO table_name (col1, col2)\nVALUES ('val1', 'val2');", category: 'Modify' },
  { label: 'INSERT multiple', sql: "INSERT INTO table_name (col1, col2)\nVALUES\n  ('val1', 'val2'),\n  ('val3', 'val4');", category: 'Modify' },
  { label: 'UPDATE rows', sql: "UPDATE table_name\nSET column = 'new_value'\nWHERE condition;", category: 'Modify' },
  { label: 'DELETE rows', sql: 'DELETE FROM table_name\nWHERE condition;', category: 'Modify' },
  { label: 'CREATE TABLE', sql: 'CREATE TABLE table_name (\n  id SERIAL PRIMARY KEY,\n  name VARCHAR(255) NOT NULL,\n  created_at TIMESTAMP DEFAULT NOW()\n);', category: 'DDL' },
  { label: 'ALTER TABLE add column', sql: 'ALTER TABLE table_name\nADD COLUMN column_name VARCHAR(255);', category: 'DDL' },
  { label: 'CREATE INDEX', sql: 'CREATE INDEX idx_name\nON table_name (column_name);', category: 'DDL' },
  { label: 'Common Table Expression', sql: 'WITH cte AS (\n  SELECT column\n  FROM table_name\n  WHERE condition\n)\nSELECT * FROM cte;', category: 'Advanced' },
  { label: 'Window Function', sql: 'SELECT\n  column,\n  ROW_NUMBER() OVER (PARTITION BY group_col ORDER BY sort_col) as rn\nFROM table_name;', category: 'Advanced' },
];

const BUILT_IN_CATEGORIES = ['Query', 'Aggregate', 'Modify', 'DDL', 'Advanced'];

// ═══════════════════════════════════════════
// Placeholder resolution
// ═══════════════════════════════════════════

function getPlaceholders(sql: string): string[] {
  const matches = sql.match(/\$\{(\w+)\}/g);
  if (!matches) return [];
  return [...new Set(matches.map((m) => m.slice(2, -1)))];
}

async function resolvePlaceholders(sql: string): Promise<string | null> {
  const placeholders = getPlaceholders(sql);
  let result = sql;
  for (const p of placeholders) {
    const value = await useDialogStore.getState().showPrompt({
      title: `Enter value for \${${p}}`,
      placeholder: p,
    });
    if (value === null) return null; // cancelled
    result = result.replace(new RegExp(`\\$\\{${p}\\}`, 'g'), value);
  }
  return result;
}

// ═══════════════════════════════════════════
// Snippet Picker component
// ═══════════════════════════════════════════

interface SnippetPickerProps {
  onSelect: (sql: string) => void;
  onClose: () => void;
}

export function SnippetPicker({ onSelect, onClose }: SnippetPickerProps) {
  const [filter, setFilter] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<CustomSnippet | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Custom snippets from settings
  const customSnippetsJson = useSettingsStore((s) => s.get('custom.snippets', '[]'));
  const settingsSave = useSettingsStore((s) => s.save);

  const customSnippets = useMemo((): CustomSnippet[] => {
    try {
      const parsed = JSON.parse(customSnippetsJson) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed as CustomSnippet[];
    } catch {
      return [];
    }
  }, [customSnippetsJson]);

  const saveCustomSnippets = useCallback(
    (snippets: CustomSnippet[]) => {
      settingsSave({ 'custom.snippets': JSON.stringify(snippets) }).catch((err: unknown) => {
        console.error('Failed to save custom snippets:', err);
      });
    },
    [settingsSave],
  );

  // Merge built-in + custom snippets
  const allSnippets = useMemo((): Snippet[] => [
    ...BUILT_IN_SNIPPETS,
    ...customSnippets.map((s) => ({
      label: s.label,
      sql: s.sql,
      category: s.category || 'Custom',
    })),
  ], [customSnippets]);

  // Compute all categories (built-in + custom)
  const allCategories = useMemo(() => {
    const customCats = customSnippets
      .map((s) => s.category || 'Custom')
      .filter((c) => !BUILT_IN_CATEGORIES.includes(c));
    return [...BUILT_IN_CATEGORIES, ...new Set(customCats)];
  }, [customSnippets]);

  // Focus search input on mount
  useEffect(() => {
    if (!editMode) {
      inputRef.current?.focus();
    }
  }, [editMode]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editingSnippet) {
          setEditingSnippet(null);
        } else if (editMode) {
          setEditMode(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, editMode, editingSnippet]);

  const filteredSnippets = useMemo(() => {
    if (!filter.trim()) return allSnippets;
    const lower = filter.toLowerCase();
    return allSnippets.filter(
      (s) =>
        s.label.toLowerCase().includes(lower) ||
        s.category.toLowerCase().includes(lower) ||
        s.sql.toLowerCase().includes(lower),
    );
  }, [filter, allSnippets]);

  const groupedSnippets = useMemo(() => {
    const groups: Record<string, Snippet[]> = {};
    for (const cat of allCategories) {
      const items = filteredSnippets.filter((s) => s.category === cat);
      if (items.length > 0) {
        groups[cat] = items;
      }
    }
    return groups;
  }, [filteredSnippets, allCategories]);

  const handleSelect = useCallback(
    async (snippet: Snippet) => {
      const placeholders = getPlaceholders(snippet.sql);
      if (placeholders.length > 0) {
        const resolved = await resolvePlaceholders(snippet.sql);
        if (resolved === null) return; // user cancelled
        onSelect(resolved);
      } else {
        onSelect(snippet.sql);
      }
      onClose();
    },
    [onSelect, onClose],
  );

  const handleDeleteCustom = useCallback(
    (id: string) => {
      const updated = customSnippets.filter((s) => s.id !== id);
      saveCustomSnippets(updated);
    },
    [customSnippets, saveCustomSnippets],
  );

  const handleNewSnippet = useCallback(() => {
    setEditingSnippet({
      id: `snippet-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      label: '',
      sql: '',
      category: 'Custom',
    });
  }, []);

  const handleSaveSnippet = useCallback(
    (snippet: CustomSnippet) => {
      if (!snippet.label.trim() || !snippet.sql.trim()) return;

      const exists = customSnippets.find((s) => s.id === snippet.id);
      const updated = exists
        ? customSnippets.map((s) => (s.id === snippet.id ? snippet : s))
        : [...customSnippets, snippet];
      saveCustomSnippets(updated);
      setEditingSnippet(null);
    },
    [customSnippets, saveCustomSnippets],
  );

  // ── Edit mode: snippet editor form ──
  if (editingSnippet) {
    return (
      <SnippetEditorForm
        snippet={editingSnippet}
        onSave={handleSaveSnippet}
        onCancel={() => setEditingSnippet(null)}
        onClose={onClose}
      />
    );
  }

  // ── Edit mode: manage custom snippets ──
  if (editMode) {
    return (
      <div
        ref={containerRef}
        className="absolute right-0 top-full mt-1 z-50 bg-dbgo-panel border rounded-md shadow-lg w-[320px] max-h-[360px] flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b flex-shrink-0">
          <button
            onClick={() => setEditMode(false)}
            className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary transition-colors"
          >
            <ArrowLeft size={12} />
            Back
          </button>
          <span className="text-xs font-semibold text-text-primary">Custom Snippets</span>
          <button
            onClick={handleNewSnippet}
            className="flex items-center gap-1 text-xs text-accent-blue hover:text-accent-blue/80 transition-colors"
          >
            <Plus size={12} />
            New
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {customSnippets.length === 0 && (
            <div className="px-3 py-6 text-2xs text-text-muted text-center">
              No custom snippets yet.
              <br />
              Click &quot;New&quot; to create one.
            </div>
          )}
          {customSnippets.map((snippet) => (
            <div
              key={snippet.id}
              className="px-3 py-2 border-b border-subtle hover:bg-dbgo-tab-hover transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-text-primary font-medium truncate">
                  {snippet.label}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => setEditingSnippet({ ...snippet })}
                    className="p-0.5 text-text-muted hover:text-text-primary transition-colors"
                    title="Edit"
                  >
                    <Edit3 size={12} />
                  </button>
                  <button
                    onClick={() => handleDeleteCustom(snippet.id)}
                    className="p-0.5 text-text-muted hover:text-accent-red transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <div className="text-[9px] text-text-muted uppercase tracking-widest mb-0.5">
                {snippet.category}
              </div>
              <div className="text-2xs text-text-muted font-mono truncate">
                {snippet.sql.split('\n')[0]}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Normal mode: pick a snippet ──
  return (
    <div
      ref={containerRef}
      className="absolute right-0 top-full mt-1 z-50 bg-dbgo-panel border rounded-md shadow-lg w-[280px] max-h-[300px] flex flex-col overflow-hidden"
    >
      {/* Search */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b flex-shrink-0">
        <Search size={11} className="text-text-muted flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search snippets..."
          className="bg-transparent text-xs text-text-primary placeholder:text-text-muted outline-none w-full"
        />
      </div>

      {/* Snippet list */}
      <div className="overflow-y-auto flex-1">
        {Object.keys(groupedSnippets).length === 0 && (
          <div className="px-3 py-3 text-2xs text-text-muted text-center">
            No matching snippets
          </div>
        )}
        {Object.entries(groupedSnippets).map(([category, snippets]) => (
          <div key={category}>
            <div className="px-3 pt-2 pb-0.5 text-[9px] font-semibold text-text-muted uppercase tracking-widest">
              {category}
            </div>
            {snippets.map((snippet, i) => (
              <button
                key={`${category}-${snippet.label}-${i}`}
                onClick={() => handleSelect(snippet)}
                className="w-full text-left px-3 py-1.5 text-xs text-text-secondary hover:bg-dbgo-tab-hover transition-colors flex flex-col gap-0.5"
              >
                <span className="text-text-primary">{snippet.label}</span>
                <span className="text-2xs text-text-muted font-mono truncate">
                  {snippet.sql.split('\n')[0]}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Manage Snippets button */}
      <div className="border-t flex-shrink-0">
        <button
          onClick={() => setEditMode(true)}
          className="w-full px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary hover:bg-dbgo-tab-hover transition-colors text-center"
        >
          Manage Snippets...
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Snippet editor form (inline)
// ═══════════════════════════════════════════

interface SnippetEditorFormProps {
  snippet: CustomSnippet;
  onSave: (snippet: CustomSnippet) => void;
  onCancel: () => void;
  onClose: () => void;
}

function SnippetEditorForm({ snippet, onSave, onCancel, onClose }: SnippetEditorFormProps) {
  const [label, setLabel] = useState(snippet.label);
  const [category, setCategory] = useState(snippet.category);
  const [sql, setSql] = useState(snippet.sql);
  const containerRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    labelRef.current?.focus();
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (!label.trim() || !sql.trim()) return;
    onSave({
      ...snippet,
      label: label.trim(),
      category: category.trim() || 'Custom',
      sql: sql,
    });
  }, [label, category, sql, snippet, onSave]);

  return (
    <div
      ref={containerRef}
      className="absolute right-0 top-full mt-1 z-50 bg-dbgo-panel border rounded-md shadow-lg w-[320px] flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b flex-shrink-0">
        <button
          onClick={onCancel}
          className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft size={12} />
          Back
        </button>
        <span className="text-xs font-semibold text-text-primary">
          {snippet.label ? 'Edit Snippet' : 'New Snippet'}
        </span>
        <div className="w-10" />
      </div>

      <div className="px-3 py-2 flex flex-col gap-2">
        <div>
          <label className="text-[9px] text-text-muted uppercase tracking-widest block mb-0.5">
            Label
          </label>
          <input
            ref={labelRef}
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="My snippet name"
            className="w-full bg-dbgo-input border rounded px-2 py-1 text-xs text-text-primary placeholder:text-text-muted outline-none focus:border-accent-blue transition-colors"
          />
        </div>

        <div>
          <label className="text-[9px] text-text-muted uppercase tracking-widest block mb-0.5">
            Category
          </label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Custom"
            className="w-full bg-dbgo-input border rounded px-2 py-1 text-xs text-text-primary placeholder:text-text-muted outline-none focus:border-accent-blue transition-colors"
          />
        </div>

        <div>
          <label className="text-[9px] text-text-muted uppercase tracking-widest block mb-0.5">
            SQL (use {'${placeholder}'} for variables)
          </label>
          <textarea
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            placeholder={'SELECT * FROM ${table_name}\nWHERE ${condition};'}
            rows={5}
            className="w-full bg-dbgo-input border rounded px-2 py-1 text-xs text-text-primary font-mono placeholder:text-text-muted outline-none focus:border-accent-blue transition-colors resize-y"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1 pb-1">
          <button
            onClick={onCancel}
            className="px-3 py-1 text-xs text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!label.trim() || !sql.trim()}
            className="flex items-center gap-1 px-3 py-1 text-xs bg-accent-blue text-white rounded hover:bg-accent-blue/80 transition-colors disabled:opacity-40 disabled:cursor-default"
          >
            <Save size={12} />
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
